import React, { useState, useRef, useEffect, useCallback } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faWandMagicSparkles,
  faImage,
  faVideo,
  faDownload,
  faSpinner,
  faLightbulb,
  faExpand,
  faBookmark,
  faTrash,
  faCopy,
  faCheck,
  faFilter,
} from "@fortawesome/free-solid-svg-icons";
import { postRequest, getRequest, deleteRequest } from "services/api/apiService";

// ── Constants ────────────────────────────────────────────────────────
const MODELS = [
  {
    id: "imagen",
    label: "Imagen 3",
    tag: "Google",
    type: "image",
    icon: faImage,
    desc: "Ultra-realistic images by Google",
    color: "from-blue-500 to-indigo-600",
  },
  {
    id: "video",
    label: "MiniMax",
    tag: "Video AI",
    type: "video",
    icon: faVideo,
    desc: "Cinematic AI video generation",
    color: "from-purple-500 to-pink-600",
  },
];

const ASPECT_RATIOS = [
  { value: "1:1",  label: "1:1",  hint: "Square" },
  { value: "16:9", label: "16:9", hint: "Landscape" },
  { value: "9:16", label: "9:16", hint: "Story" },
  { value: "4:5",  label: "4:5",  hint: "Portrait" },
];

const STYLE_PRESETS = [
  "Photorealistic",
  "Minimal & Clean",
  "Bold & Vibrant",
  "Luxury",
  "Playful",
  "Cinematic",
];

const PROMPT_EXAMPLES = [
  "A sleek smartphone on a marble surface with soft studio lighting, premium product ad",
  "Young woman smiling with coffee, warm lifestyle photography, brand campaign",
  "Luxury watch floating in water splashes, dramatic dark background, high-end advertisement",
  "Colorful sneakers on a white background, energetic, Gen Z fashion brand",
];

// ── Helpers ──────────────────────────────────────────────────────────
const aspectClass = (ar) => {
  const map = { "1:1": "aspect-square", "16:9": "aspect-video", "9:16": "aspect-[9/16]", "4:5": "aspect-[4/5]" };
  return map[ar] || "aspect-square";
};

const MAX_PROMPT_LENGTH = 500;

// Unicode-code-point-safe length/truncate (BUG-012 / CT-078). `.length`/`.slice()`
// on a raw string count UTF-16 code units, so a plain string.slice(0, n) can cut an
// emoji's surrogate pair in half and .length overcounts emojis (2+ units each).
// Array.from() splits by code point instead, so basic emoji are never split mid-
// character. Note: multi-codepoint sequences (ZWJ family emoji, skin-tone
// modifiers) still count as more than one unit here — that would need
// Intl.Segmenter's grapheme-cluster mode, not needed for this fix.
const codePointLength = (str) => Array.from(str).length;
const truncateToCodePoints = (str, max) => Array.from(str).slice(0, max).join("");

const IMAGE_ENGINES = [
  { value: "auto",   label: "Auto",         badge: null,        desc: "Best available (Imagen → FLUX)" },
  { value: "imagen", label: "Imagen 4",     badge: "Google",    desc: "High quality, may be slower" },
  { value: "flux",   label: "FLUX Schnell", badge: "Open",      desc: "Fast, always available" },
];

const ENGINE_LABEL = {
  imagen4:     "Imagen 4",
  huggingface: "HuggingFace",
  replicate:   "FLUX Schnell",
  minimax:     "MiniMax",
};

// ── Ad Library ───────────────────────────────────────────────────────
const AdLibrary = ({ onUsePrompt }) => {
  const [assets, setAssets]         = useState([]);
  const [loading, setLoading]       = useState(true);
  const [typeFilter, setTypeFilter] = useState("all");
  const [copied, setCopied]         = useState(null);
  const [fullscreen, setFullscreen] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const q = typeFilter !== "all" ? `?type=${typeFilter}` : "";
      const res = await getRequest(`/api/ai-ad/library${q}`);
      setAssets(res.assets || []);
    } catch { /* silent */ } finally {
      setLoading(false);
    }
  }, [typeFilter]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this asset?")) return;
    await deleteRequest(`/api/ai-ad/library/${id}`);
    setAssets((prev) => prev.filter((a) => a.id !== id));
  };

  const handleCopy = (url, id) => {
    navigator.clipboard.writeText(url);
    setCopied(id);
    setTimeout(() => setCopied(null), 1500);
  };

  const fmtDate = (iso) => {
    const d = new Date(iso);
    return isNaN(d) ? "—" : d.toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" });
  };

  return (
    <div className="flex flex-col h-full min-h-0">
      {/* Toolbar */}
      <div className="flex items-center gap-2 mb-4 shrink-0">
        <FontAwesomeIcon icon={faFilter} className="text-gray-400 text-xs" />
        {["all", "image", "video"].map((f) => (
          <button key={f} onClick={() => setTypeFilter(f)}
            className={`text-xs px-3 py-1.5 rounded-full border transition-all capitalize ${
              typeFilter === f ? "bg-[#221C51] text-white border-[#221C51]" : "bg-white text-gray-500 border-gray-200 hover:border-gray-400"
            }`}>
            {f === "all" ? "All" : f === "image" ? "Images" : "Videos"}
          </button>
        ))}
        <span className="ml-auto text-xs text-gray-400">{assets.length} asset{assets.length !== 1 ? "s" : ""}</span>
      </div>

      {loading && (
        <div className="flex-1 flex items-center justify-center text-gray-400 text-sm">
          <FontAwesomeIcon icon={faSpinner} spin className="mr-2" /> Loading…
        </div>
      )}

      {!loading && assets.length === 0 && (
        <div className="flex-1 flex flex-col items-center justify-center text-center gap-2 text-gray-400">
          <FontAwesomeIcon icon={faBookmark} className="text-4xl text-gray-200" />
          <p className="text-sm font-medium">Your library is empty</p>
          <p className="text-xs">Generated images and videos are saved here automatically</p>
        </div>
      )}

      {!loading && assets.length > 0 && (
        <div className="flex-1 overflow-y-auto">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {assets.map((a) => (
              <div key={a.id} className="group relative bg-white border border-gray-100 rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-all">
                {/* Thumbnail */}
                <div className="relative aspect-square bg-gray-100 cursor-pointer" onClick={() => setFullscreen(a)}>
                  {a.type === "image" ? (
                    <img src={a.url} alt={a.prompt || "generated"} className="w-full h-full object-cover" />
                  ) : (
                    <video src={a.url} className="w-full h-full object-cover" muted />
                  )}
                  {/* Hover overlay */}
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
                    <FontAwesomeIcon icon={faExpand} className="text-white text-lg drop-shadow" />
                  </div>
                  {/* Type badge */}
                  <span className={`absolute top-2 left-2 text-[10px] font-bold px-2 py-0.5 rounded-full ${
                    a.type === "video" ? "bg-purple-600 text-white" : "bg-blue-600 text-white"
                  }`}>
                    {a.type === "video" ? "VIDEO" : "IMG"}
                  </span>
                </div>

                {/* Info */}
                <div className="p-2.5">
                  <p className="text-[11px] text-gray-600 leading-snug line-clamp-2 mb-1.5">
                    {a.prompt || <span className="text-gray-300 italic">No prompt</span>}
                  </p>
                  <div className="flex items-center justify-between gap-1 flex-wrap">
                    <span className="text-[10px] text-gray-400">{fmtDate(a.created_at || a.createdAt)}</span>
                    {a.engine && (
                      <span className="text-[10px] bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded-full">
                        {ENGINE_LABEL[a.engine] || a.engine}
                      </span>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex gap-1 mt-2">
                    <button
                      onClick={() => onUsePrompt(a.prompt)}
                      title="Reuse prompt"
                      className="flex-1 text-[10px] py-1 rounded-lg bg-[#221C51]/5 text-[#221C51] hover:bg-[#221C51]/10 font-semibold transition-all"
                    >
                      Reuse
                    </button>
                    <button
                      onClick={() => handleCopy(a.url, a.id)}
                      title="Copy URL"
                      className="w-7 h-7 flex items-center justify-center rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-500 transition-all"
                    >
                      <FontAwesomeIcon icon={copied === a.id ? faCheck : faCopy} className="text-[10px]" />
                    </button>
                    <a
                      href={a.url} download target="_blank" rel="noreferrer"
                      title="Download"
                      className="w-7 h-7 flex items-center justify-center rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-500 transition-all"
                    >
                      <FontAwesomeIcon icon={faDownload} className="text-[10px]" />
                    </a>
                    <button
                      onClick={() => handleDelete(a.id)}
                      title="Delete"
                      className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-red-50 text-gray-300 hover:text-red-500 transition-all"
                    >
                      <FontAwesomeIcon icon={faTrash} className="text-[10px]" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Fullscreen lightbox */}
      {fullscreen && (
        <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4" onClick={() => setFullscreen(null)}>
          {fullscreen.type === "video"
            ? <video src={fullscreen.url} controls autoPlay loop className="max-w-full max-h-full rounded-xl" onClick={(e) => e.stopPropagation()} />
            : <img src={fullscreen.url} alt="fullscreen" className="max-w-full max-h-full rounded-xl object-contain" />
          }
          <button onClick={() => setFullscreen(null)}
            className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 text-white hover:bg-white/20 flex items-center justify-center text-lg">
            ×
          </button>
          <div className="absolute bottom-4 left-0 right-0 text-center">
            <p className="text-white/70 text-xs">{fullscreen.prompt}</p>
          </div>
        </div>
      )}
    </div>
  );
};

// ── Component ────────────────────────────────────────────────────────
const AdAiLauncher = () => {
  const [tab, setTab]                 = useState("generate"); // "generate" | "library"
  const [model, setModel]             = useState("imagen");
  const [engine, setEngine]           = useState("auto");
  const [prompt, setPrompt]           = useState("");
  const [aspectRatio, setAspectRatio] = useState("1:1");
  const [stylePreset, setStylePreset] = useState("");
  const [loading, setLoading]         = useState(false);
  const [result, setResult]           = useState(null); // { urls?, url?, type, engine }
  const [error, setError]             = useState(null);
  const [activeImg, setActiveImg]     = useState(0);
  const [fullscreen, setFullscreen]   = useState(false);
  const textareaRef = useRef(null);

  const handleUsePrompt = (p) => {
    if (p) setPrompt(p);
    setTab("generate");
    setTimeout(() => textareaRef.current?.focus(), 100);
  };

  const selectedModel = MODELS.find((m) => m.id === model);

  const buildPrompt = () => {
    let p = prompt.trim();
    if (stylePreset) p += `, ${stylePreset} style`;
    return p;
  };

  const handleGenerate = async () => {
    const finalPrompt = buildPrompt();
    if (!finalPrompt) return;
    setLoading(true);
    setError(null);
    setResult(null);
    setActiveImg(0);

    try {
      if (model === "imagen") {
        const data = await postRequest("/api/ai-ad/imagen", {
          prompt: finalPrompt,
          aspectRatio,
          sampleCount: 2,
          engine,
        });
        setResult({ urls: data.urls, type: "image", engine: data.engine });
      } else if (model === "video") {
        const data = await postRequest("/api/ai-ad/video", {
          prompt: finalPrompt,
          aspectRatio,
        });
        setResult({ url: data.url, type: "video" });
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Generation failed");
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (url) => {
    const a = document.createElement("a");
    a.href = url;
    a.download = `onebotads-ai-${Date.now()}.${result?.type === "video" ? "mp4" : "png"}`;
    a.target = "_blank";
    a.click();
  };

  const injectExample = (ex) => {
    setPrompt(ex);
    textareaRef.current?.focus();
  };

  const currentUrls = result?.type === "image" ? (result.urls || []) : [];
  const currentUrl  = result?.type === "image" ? currentUrls[activeImg] : result?.url;

  return (
    <div className="flex flex-col h-full overflow-hidden">

      {/* ── Header ── */}
      <div className="flex items-center gap-3 mb-5 shrink-0">
        <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#221C51] to-[#4A3D9A] flex items-center justify-center shadow">
          <FontAwesomeIcon icon={faWandMagicSparkles} className="text-white text-sm" />
        </div>
        <div>
          <h1 className="text-lg font-bold text-[#221C51] leading-tight">AI Ad Launcher</h1>
          <p className="text-xs text-gray-400">Generate creatives with AI</p>
        </div>
        {/* Tab toggle */}
        <div className="ml-auto flex gap-1 bg-gray-100 rounded-xl p-1">
          <button onClick={() => setTab("generate")}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
              tab === "generate" ? "bg-[#221C51] text-white shadow" : "text-gray-500 hover:text-[#221C51]"
            }`}>
            <FontAwesomeIcon icon={faWandMagicSparkles} className="text-[10px]" /> Generate
          </button>
          <button onClick={() => setTab("library")}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
              tab === "library" ? "bg-[#221C51] text-white shadow" : "text-gray-500 hover:text-[#221C51]"
            }`}>
            <FontAwesomeIcon icon={faBookmark} className="text-[10px]" /> Library
          </button>
        </div>
      </div>

      {/* ── Library tab ── */}
      {tab === "library" && (
        <div className="flex-1 overflow-hidden min-h-0">
          <AdLibrary onUsePrompt={handleUsePrompt} />
        </div>
      )}

      {/* ── Two-panel layout (Generate tab) ── */}
      {tab === "generate" && <div className="flex flex-col lg:flex-row gap-5 flex-1 overflow-hidden min-h-0">

        {/* ════ LEFT — Controls ════ */}
        <div className="w-full lg:w-[380px] shrink-0 overflow-y-auto space-y-4 pr-1">

          {/* Model selector */}
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Model</p>
            <div className="grid grid-cols-2 gap-2">
              {MODELS.map((m) => (
                <button
                  key={m.id}
                  onClick={() => { setModel(m.id); setResult(null); setError(null); }}
                  className={`relative flex flex-col items-start gap-1 p-3 rounded-xl border-2 text-left transition-all ${
                    model === m.id
                      ? "border-[#221C51] bg-[#221C51]/5 shadow-sm"
                      : "border-gray-100 bg-white hover:border-gray-300"
                  }`}
                >
                  <div className="flex items-center gap-2 w-full">
                    <div className={`w-7 h-7 rounded-lg bg-gradient-to-br ${m.color} flex items-center justify-center`}>
                      <FontAwesomeIcon icon={m.icon} className="text-white text-xs" />
                    </div>
                    <div>
                      <p className={`text-xs font-bold leading-none ${model === m.id ? "text-[#221C51]" : "text-gray-700"}`}>{m.label}</p>
                      <p className="text-[10px] text-gray-400">{m.tag}</p>
                    </div>
                    {model === m.id && (
                      <span className="ml-auto w-2 h-2 rounded-full bg-[#221C51]" />
                    )}
                  </div>
                  <p className="text-[10px] text-gray-400 pl-9">{m.desc}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Prompt */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Prompt</p>
              <span className="text-[10px] text-gray-400">{codePointLength(prompt)}/{MAX_PROMPT_LENGTH}</span>
            </div>
            <textarea
              ref={textareaRef}
              value={prompt}
              onChange={(e) => setPrompt(truncateToCodePoints(e.target.value, MAX_PROMPT_LENGTH))}
              onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) handleGenerate(); }}
              placeholder={`Describe your ad creative...\n\nTip: Be specific about lighting, mood, product, and target audience.`}
              rows={5}
              className="w-full text-sm text-gray-700 bg-gray-50 border border-gray-200 rounded-xl px-3.5 py-3 resize-none focus:outline-none focus:ring-2 focus:ring-[#221C51]/20 focus:border-[#221C51]/40 placeholder-gray-300 leading-relaxed"
            />

            {/* Example prompts */}
            <div className="mt-2">
              <div className="flex items-center gap-1 mb-1.5">
                <FontAwesomeIcon icon={faLightbulb} className="text-amber-400 text-[10px]" />
                <span className="text-[10px] text-gray-400 font-medium">Examples</span>
              </div>
              <div className="flex flex-col gap-1">
                {PROMPT_EXAMPLES.map((ex, i) => (
                  <button
                    key={i}
                    onClick={() => injectExample(ex)}
                    className="text-left text-[10px] text-gray-400 hover:text-[#221C51] hover:bg-[#221C51]/5 px-2 py-1 rounded-lg transition-all truncate"
                  >
                    → {ex}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Aspect ratio */}
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Aspect Ratio</p>
            <div className="grid grid-cols-4 gap-1.5">
              {ASPECT_RATIOS.map((ar) => (
                <button
                  key={ar.value}
                  onClick={() => setAspectRatio(ar.value)}
                  className={`flex flex-col items-center py-2 px-1 rounded-xl border-2 transition-all ${
                    aspectRatio === ar.value
                      ? "border-[#221C51] bg-[#221C51]/5 text-[#221C51]"
                      : "border-gray-100 bg-white text-gray-500 hover:border-gray-300"
                  }`}
                >
                  <span className="text-xs font-bold leading-none">{ar.label}</span>
                  <span className="text-[9px] text-gray-400 mt-0.5">{ar.hint}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Engine selector — image only */}
          {selectedModel?.type === "image" && (
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Engine</p>
              <div className="flex gap-1.5">
                {IMAGE_ENGINES.map((e) => (
                  <button
                    key={e.value}
                    onClick={() => setEngine(e.value)}
                    title={e.desc}
                    className={`flex-1 flex flex-col items-center py-2 px-1 rounded-xl border-2 transition-all ${
                      engine === e.value
                        ? "border-[#221C51] bg-[#221C51]/5 text-[#221C51]"
                        : "border-gray-100 bg-white text-gray-500 hover:border-gray-300"
                    }`}
                  >
                    <span className="text-xs font-bold leading-none">{e.label}</span>
                    {e.badge && <span className="text-[9px] text-gray-400 mt-0.5">{e.badge}</span>}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Style preset */}
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Style <span className="text-gray-300 font-normal">(optional)</span></p>
            <div className="flex flex-wrap gap-1.5">
              {STYLE_PRESETS.map((s) => (
                <button
                  key={s}
                  onClick={() => setStylePreset(stylePreset === s ? "" : s)}
                  className={`text-xs px-3 py-1.5 rounded-full border transition-all ${
                    stylePreset === s
                      ? "bg-[#221C51] text-white border-[#221C51]"
                      : "bg-white text-gray-500 border-gray-200 hover:border-gray-400"
                  }`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>

          {/* Generate button */}
          <button
            onClick={handleGenerate}
            disabled={!prompt.trim() || loading}
            className="w-full flex items-center justify-center gap-2.5 py-3.5 rounded-xl bg-gradient-to-r from-[#221C51] to-[#4A3D9A] text-white font-semibold text-sm shadow-md transition-all hover:shadow-lg hover:scale-[1.01] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
          >
            {loading ? (
              <>
                <FontAwesomeIcon icon={faSpinner} spin />
                <span>Generating{selectedModel?.type === "video" ? " video" : " image"}…</span>
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faWandMagicSparkles} />
                <span>Generate with {selectedModel?.label}</span>
                <span className="text-white/50 text-xs">⌘↵</span>
              </>
            )}
          </button>

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-xs text-red-600">
              {error}
            </div>
          )}
        </div>

        {/* ════ RIGHT — Preview ════ */}
        <div className="flex-1 flex flex-col min-w-0 min-h-0">
          <div className="flex items-center justify-between mb-2 shrink-0">
            <div className="flex items-center gap-2">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Preview</p>
              {result?.engine && (
                <span className="text-[10px] bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full font-medium">
                  via {result.engine === "imagen4" ? "Imagen 4" : result.engine === "huggingface" ? "HuggingFace FLUX" : result.engine === "replicate" ? "FLUX Schnell" : result.engine}
                </span>
              )}
            </div>
            {result && currentUrl && (
              <div className="flex items-center gap-2">
                {result.type === "image" && currentUrls.length > 1 && (
                  <div className="flex gap-1">
                    {currentUrls.map((_, i) => (
                      <button
                        key={i}
                        onClick={() => setActiveImg(i)}
                        className={`w-5 h-5 rounded text-[10px] font-bold transition-all ${
                          activeImg === i ? "bg-[#221C51] text-white" : "bg-gray-100 text-gray-500 hover:bg-gray-200"
                        }`}
                      >
                        {i + 1}
                      </button>
                    ))}
                  </div>
                )}
                <button
                  onClick={() => setFullscreen(true)}
                  title="Fullscreen"
                  className="w-7 h-7 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-500 flex items-center justify-center transition-all"
                >
                  <FontAwesomeIcon icon={faExpand} className="text-xs" />
                </button>
                <button
                  onClick={() => handleDownload(currentUrl)}
                  title="Download"
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#221C51] text-white text-xs font-medium hover:bg-[#2d2566] transition-all"
                >
                  <FontAwesomeIcon icon={faDownload} className="text-[10px]" />
                  Download
                </button>
              </div>
            )}
          </div>

          {/* Preview area */}
          <div className="flex-1 rounded-2xl border-2 border-dashed border-gray-200 bg-gray-50 flex items-center justify-center overflow-hidden relative min-h-0">
            {loading && (
              <div className="flex flex-col items-center gap-3 text-center px-6">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#221C51] to-[#4A3D9A] flex items-center justify-center shadow-lg animate-pulse">
                  <FontAwesomeIcon icon={faWandMagicSparkles} className="text-white text-xl" />
                </div>
                <p className="text-sm font-medium text-[#221C51]">
                  {selectedModel?.type === "video" ? "Generating video…" : "Generating image…"}
                </p>
                <p className="text-xs text-gray-400">
                  {selectedModel?.type === "video"
                    ? "Video generation takes 2–4 minutes"
                    : "Imagen 3 takes ~15 seconds"}
                </p>
                <div className="flex gap-1 mt-1">
                  {[0, 1, 2].map((i) => (
                    <span
                      key={i}
                      className="w-2 h-2 rounded-full bg-[#221C51]/30 animate-bounce"
                      style={{ animationDelay: `${i * 0.15}s` }}
                    />
                  ))}
                </div>
              </div>
            )}

            {!loading && !result && (
              <div className="flex flex-col items-center gap-2 text-center px-8 select-none">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center mb-2">
                  <FontAwesomeIcon icon={faImage} className="text-gray-300 text-2xl" />
                </div>
                <p className="text-sm font-medium text-gray-400">Your creative will appear here</p>
                <p className="text-xs text-gray-300">Write a prompt and click Generate</p>
              </div>
            )}

            {!loading && result && result.type === "image" && currentUrls[activeImg] && (
              <img
                src={currentUrls[activeImg]}
                alt="AI generated ad"
                className="max-w-full max-h-full object-contain rounded-xl shadow-lg"
                style={{ maxHeight: "calc(100% - 16px)" }}
              />
            )}

            {!loading && result && result.type === "video" && result.url && (
              <video
                src={result.url}
                controls
                autoPlay
                loop
                className="max-w-full max-h-full rounded-xl shadow-lg"
                style={{ maxHeight: "calc(100% - 16px)" }}
              />
            )}
          </div>

          {/* Thumbnail strip for multiple images */}
          {!loading && result?.type === "image" && currentUrls.length > 1 && (
            <div className="flex gap-2 mt-2 shrink-0">
              {currentUrls.map((url, i) => (
                <button
                  key={i}
                  onClick={() => setActiveImg(i)}
                  className={`h-14 w-14 rounded-xl overflow-hidden border-2 transition-all shrink-0 ${
                    activeImg === i ? "border-[#221C51] shadow-md" : "border-transparent hover:border-gray-300"
                  }`}
                >
                  <img src={url} alt={`variant ${i + 1}`} className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      } {/* end generate tab */}

      {/* ── Fullscreen lightbox ── */}
      {fullscreen && currentUrl && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
          onClick={() => setFullscreen(false)}
        >
          {result?.type === "video" ? (
            <video src={currentUrl} controls autoPlay loop className="max-w-full max-h-full rounded-xl" onClick={(e) => e.stopPropagation()} />
          ) : (
            <img src={currentUrl} alt="fullscreen" className="max-w-full max-h-full rounded-xl object-contain" />
          )}
          <button
            onClick={() => setFullscreen(false)}
            className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 text-white hover:bg-white/20 flex items-center justify-center text-lg"
          >
            ×
          </button>
        </div>
      )}
    </div>
  );
};

export default AdAiLauncher;
